import {Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {Order} from "../../../model/order";
import {DownloadService} from "../../../service/download.service";
import {ProductService} from "../../../service/product.service";
import {OrderService} from "../../../service/order.service";
import {ActivatedRoute} from "@angular/router";
import {SlideInOutAnimation} from "../../../service/animations.service";
import {Property} from "../../../model/property";
import {PropertyService} from "../../../service/property.service";
import {NgxIndexedDBService} from "ngx-indexed-db";
import {filter, map, takeUntil} from "rxjs/operators";
import {Observable, Subject} from "rxjs";
import {StorageService} from "../../../service/storage.service";
import {LoginIdService} from "../../../service/login-id.service";
import {Document} from "../../../model/document";
import {Product} from "../../../model/product";
import {ViewName} from "../../../model/viewname";
import {IdBuilderUtils} from "../../../utils/id-builder-utils";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import {DocumentService} from "../../../service/document.service";
import {HttpHeaders} from "@angular/common/http";
import {DocumentFile} from "../../../model/document-file";
import {DocumentFolder} from "../../../model/documentFolder";
import DocumentFolderUtils from "../../../utils/document-folder-utils";
import NameFinderUtils from "../../../utils/name-finder-utils";
import {DocumentRowComponent} from "../document-row/document-row.component";
import {PlanungDocumentRowComponent} from "../planung-document-row/planung-document-row.component";
import * as Sentry from "@sentry/angular";
import {NewCustomerInteractionResult} from "../customer-interaction/customer-interaction.component";
import CustomerInteractionUtils from "../../../utils/customer-interaction-utils";

@Component({
  selector: 'app-order-object',
  templateUrl: './order-object.component.html',
  styleUrls: ['./order-object.component.css'],
  animations: [SlideInOutAnimation]
})
export class OrderObjectComponent implements OnInit, OnDestroy {

    @ViewChildren('documentRowComponent') documentRowComponents: QueryList<DocumentRowComponent>;
    @ViewChild('planungDocumentRowComponent') planungDocumentRowComponent: PlanungDocumentRowComponent

    protected readonly faArrowLeft = faArrowLeft;

    products = [];
    productsByCategory: Map<string, Product[]>;
    order: Order;
    animationState = 'out';
    today: Date;
    property: Property;
    planungDocuments: Document[] = [];
    planungDocumentNames: string[] = [];

    searchText: string;
    orders = [];
    foundDocuments: boolean = false;
    batchStarted: boolean = false;
    stopFilesAndFolderInitialization: boolean = false;
    indexDBOrderObserver: Observable<Order>;

    showCustomerInteractionModal: boolean = false;
    // Feedback for the user after a new customer interaction was created
    customerInteractionNotification: { content: string, error: boolean } = null;
    batchingPlanungDocumentsObservable: Subject<boolean> = new Subject<boolean>();
    private stopRequestObservable: Subject<void> = new Subject<void>()

    tooltipIconArray = [
      { key: "Bestellung erfolgreich", image: "assets/images/status-messages/order_placed_icon.svg" },
      { key: "Auskunft in Bearbeitung", image: "assets/images/status-messages/information_in_progress_icon.svg" },
      { key: "Auskunft storniert", image: "assets/images/status-messages/order_canceled_icon.svg" },
      { key: "Handlungsaufforderung", image: "assets/images/status-messages/call_to_action_icon.svg" },
      { key: "Auskunft ist eingetroffen", image: "assets/images/status-messages/information_received_icon.svg" },
      { key: "Download", image: "assets/images/download_icon/download_icon_light.svg" }
    ];

    constructor(private downloadService: DownloadService,
                private loginIdService: LoginIdService,
                private productService: ProductService,
                private orderService: OrderService,
                private propertyService: PropertyService,
                private route: ActivatedRoute,
                private dbService: NgxIndexedDBService,
                private storageService: StorageService,
                private documentService: DocumentService) {
    }

  ngOnDestroy(): void {
      this.cancelFilesAndFolderRequests();
  }

  async ngOnInit(): Promise<any> {
    await this.loginIdService.loginServiceInitialized$.toPromise();

    let propertyId = this.route.snapshot.params.propertyid;

        this.storageService.listeningForOrderObjectDownload = false;
        this.indexDBOrderObserver = this.storageService.indexDBChange$.pipe(
                filter(({key}) => key === "orders"),
                map(orders => orders?.value)
        );

        this.indexDBOrderObserver.subscribe((data) => {
            if (this.storageService.listeningForOrderObjectDownload) {
                this.order.orderStatistics = data.orderStatistics;
                this.property.propertyStatistics = data.properties.find(p => p.id === propertyId)?.propertyStatistics
                this.initPlanungDocumentsFromProperty();
                this.storageService.listeningForOrderObjectDownload = false;
            }
        });

        this.productsByCategory = await this.productService.getProductByCategory().toPromise();

        this.planungDocumentNames = this.productsByCategory["Planungsrechtsauskünfte"].map((product: Product) => product.nameShort)


      this.dbService.getByKey('orders', IdBuilderUtils.buildOrderIdFromDocumentOrPropertyId(propertyId)).subscribe((order: Order) => {
          if(order){
              this.order = order;
          }
          else{
              this.orderService.getChildlessOrderById(IdBuilderUtils.buildOrderIdFromDocumentOrPropertyId(propertyId)).subscribe((res) => {
                  this.order = res;
              })
          }
      })

        this.propertyService.getPropertyById(propertyId, ViewName.PROPERTY_PLATFORM_VIEW).subscribe(async (res) => {
          this.property = res;
          this.sortCustomerInteractions();
          this.sortStatusInformationDates();
          this.sortDocumentFilesAndFolderNames();
          this.sortDocuments();
          this.initPlanungDocumentsFromProperty()

          this.setAllDocumentLoadingStatus(true);
          this.fetchFilesAndFolders().then();
        });

        this.productService.getProducts().subscribe((products) => {
            this.products = products;
        })
    }

  /**
   * Fetches files and folders for each document in batches.
   *
   * @returns Promise that resolves when all files and folders for all documents have been fetched and assigned.
   */
  private async fetchFilesAndFolders(): Promise<void> {
    const batchSize = 5;
    const totalDocuments = this.property.documents.length;
    const batchedDocuments: Document[] = [];

    for (let index = 0; index < totalDocuments; index += batchSize) {

      const documentBatches = this.property.documents.slice(index, index + batchSize);

      const promises = documentBatches.map(async document => {
        try {
          if (this.stopFilesAndFolderInitialization) {
            return;
          }

          const documentFilesPromise = this.documentService.getDocumentFilesByDocumentId(document.docid, {headers: new HttpHeaders({'showing-spinner': ''})}).pipe(takeUntil(this.stopRequestObservable)).toPromise();
          const documentFoldersPromise = this.documentService.getDocumentFoldersByDocumentId(document.docid, {headers: new HttpHeaders({'showing-spinner': ''})}).pipe(takeUntil(this.stopRequestObservable)).toPromise();

          const results = await Promise.all([documentFilesPromise, documentFoldersPromise]);
          const [documentFileResult, documentFolderResult] = results;
          document.files = documentFileResult;
          document.folders = documentFolderResult;
          this.mapFilesToFolders(documentFileResult, document.folders);
          document.isLoadingFilesAndFolder = false;
        } catch (error) {
          Sentry.captureException(error);
          this.setAllDocumentLoadingStatus(false);
        }
      })
      await Promise.all(promises);
      batchedDocuments.push(...documentBatches);

      if (this.containsAllDocuments(batchedDocuments, this.planungDocuments)) {
        this.batchingPlanungDocumentsObservable.next(true);
      }
    }
  }

    async downloadByProperty(property: Property) {
      const isAnyDocumentStillLoadingFilesAndFolders = property.documents.some(document => document.isLoadingFilesAndFolder);
      const subFolderDepth = DocumentFolderUtils.getMaxSubfolderDepthInPropertyDocuments(property.documents);
      this.storageService.listeningForOrderObjectDownload = true;
      this.batchStarted = await this.downloadService.downloadForProperty(property, false, false, isAnyDocumentStillLoadingFilesAndFolders || subFolderDepth >= 2);
    }

    progress() {
        let amountOfDocs = this.property.documents.length;
        let receivedDocs = this.property.documents.filter(d => d.received).length;
        return receivedDocs / amountOfDocs * 100;
    }

    private sortCustomerInteractions(){
        this.property.customerInteractions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }

  /**
   * Sorts the documents of the current property (this.property) by their long name alphabetically
   */
  private sortDocuments(): void {
      this.property.documents.sort((a, b) => NameFinderUtils.getProductLongName(a.docname, this.products, null).localeCompare(NameFinderUtils.getProductLongName(b.docname, this.products, null)));
    }

    sortStatusInformationDates() {
        this.property.documents.forEach(d => {
            d.statusInformation.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        })
    }

    sortDocumentFilesAndFolderNames() {
        // PROPERTY_PLATFORM_VIEW does not request files and folders, so they are null here until
        // fetchFilesAndFolders() has loaded them.
        this.property.documents.forEach(d => {
            d.files?.sort((a, b) => a.fileName.localeCompare(b.fileName));
            d.folders?.sort((a, b) => a.folderName.localeCompare(b.folderName));
            d.folders?.forEach(f => {
                f.files?.sort((a, b) => a.fileName.localeCompare(b.fileName));
            })
        })
    }

    setFoundDocuments() {
        let numberOfFoundDocuments = document.querySelectorAll('#outer-container #document').length;
        this.foundDocuments = numberOfFoundDocuments > 0;
    }

  amountOfNewDocs(property: Property) {
    let amount = 0;
    property.documents.forEach(document =>
      amount += document.files.filter(file => !file.downloaded).length
    )
    return amount;
  }

  hasDownloadPermissions(): boolean {
    if(this.order) {
      return this.loginIdService.hasPermissionsForOrder(this.order, 'DOWNLOAD');
    }
  }

  hasViewPermissions(): boolean {
    if(this.order) {
      return this.loginIdService.hasPermissionsForOrder(this.order, 'VIEW');
    }
  }

  showToolTip() {
    return this.hasDownloadPermissions() ? '' : 'Sie haben nicht die erforderlichen Berechtigungen, um diese Dokumente herunterzuladen!';
  }

  updateBatchStarted(batchStarted: boolean){
      this.batchStarted = batchStarted;
  }

    /**
     * Initializes this.planungDocuments.
     * this.planungDocuments contains all documents from this.property which are from the category "Planungsrechtsauskünfte"
     */
    public initPlanungDocumentsFromProperty(): void {
        this.property.documents.forEach(document => {
            if (this.planungDocumentNames.includes(document.docname)) {
                this.planungDocuments.push(document);
            }
        });
    }

    /**
     * Closes the customer interaction modal and updates the property customer interaction with the given customer interaction.
     * Afterwards the user is informed whether the request and its attached files were submitted successfully.
     *
     * @param customerInteraction CustomerInteraction which should be included in the current property
     * @param uploadedFileCount Number of attached files that were uploaded successfully
     * @param failedFileNames Names of the attached files that could not be uploaded
     */
    public closeCiModalAndUpdateProperty({customerInteraction, uploadedFileCount = 0, failedFileNames = []}: NewCustomerInteractionResult): void{
        this.property.customerInteractions.push(customerInteraction);
        this.sortCustomerInteractions();
        this.showCustomerInteractionModal = false;
        this.customerInteractionNotification = CustomerInteractionUtils.buildNewCustomerInteractionNotification(uploadedFileCount, failedFileNames);
    }

  /**
   * Cancel all running requests for getting files and folders
   */
  public cancelFilesAndFolderRequests(): void {
    this.stopFilesAndFolderInitialization = true;
    this.stopRequestObservable.next();
  }

  /**
   * Checks if all documents in one array are present in another array based on their document id
   *
   * @param documentArray1 First array of documents
   * @param documentArray2 Second array of documents to check against
   * @return Ture if all documents in documentArray2 are present in documentArray1 otherwise false
   */
  private containsAllDocuments(documentArray1: Document[], documentArray2: Document[]) {
    // Check if every document in documentArray2 is present in documentArray1
    return documentArray2.every(document1 => documentArray1.some(document2 => document1.docid === document2.docid));
  }

  /**
   * Maps files to their corresponding folders within a document.
   *
   * @param retrievedDocumentFiles Array of document file objects which should be mapped to their corresponding folders
   * @param documentFolders Array of document folders to which the documentFiles should be mapped
   */
  private mapFilesToFolders(retrievedDocumentFiles: DocumentFile[], documentFolders: DocumentFolder[]): void {
    const flattenedFolders = DocumentFolderUtils.flattenFolders(documentFolders);

    retrievedDocumentFiles.forEach(file => {
      if (file.documentFolderId) {
        const documentFolder = flattenedFolders.find(folder => folder.id === file.documentFolderId);
        if(!documentFolder){
          file.documentFolderId = null;
          return;
        }
        if(!documentFolder.files){
          documentFolder.files = []; // Initialize the files array if it doesn't exist
        }
        documentFolder.files.push(file);
      }
    });
  }

  /**
   * Updates the loading status of all property document with the passed value
   *
   * @param newLoadingStatus New loading status for all property documents
   */
  private setAllDocumentLoadingStatus(newLoadingStatus: boolean): void {
    this.property.documents.forEach(document => {
      document.isLoadingFilesAndFolder = newLoadingStatus;
    });
  }

}
